import {
  BERTH_SIDES,
  CARGO_OPERATIONS,
  freshWaterClosing,
  isDailyKind,
  LOAD_CONDITIONS,
  nextOperationOptions,
  phaseReference,
  REPORT_SECTIONS,
  type ReportSection,
  reportRob,
  VOYAGE_REPORT_KINDS,
  VOYAGE_REPORT_SCHEMA_VERSION,
  type VoyageReportKind,
  WAITING_REASONS,
} from '@felix/domain';
import { type FormConversion, type NoonFormValues, valuesToInput } from './noon-form.js';
import { formatLatitude, formatLongitude } from './noon-report.js';
import type { Voyage, VoyageReport } from './types.js';

/**
 * Leg / Event formu (M11c2): Noon dışındaki olay raporları tek formla girilir; alanlar türe göre
 * bu tablodan gelir (`docs/gereksinimler/sefer-raporlama.md` "Rapor Alan Matrisi"). Zorunlu
 * işaretleri şemayla aynıdır (testli); doğrulama yine `parseVoyageReport`.
 */
export type EventKind = Exclude<VoyageReportKind, 'noon'>;
export const EVENT_KINDS = VOYAGE_REPORT_KINDS.filter((k): k is EventKind => k !== 'noon');

export type EventFieldType = 'text' | 'datetime' | 'number' | 'select' | 'note';

export interface EventField {
  key: string;
  type: EventFieldType;
  required: boolean;
  unit?: string;
  /** Sayı alanının ondalık hanesi (varsayılan 1; tam sayıda 0). */
  decimals?: number;
  /** `select` için değerler; ekrandaki ad `voyage.event.options.<liste>.<değer>`. */
  options?: { list: string; values: readonly string[] };
  /** Eski alan: yalnız değeri varsa gösterilir (ör. Anchorage Daily'nin tek sayılık tüketimi). */
  legacy?: boolean;
}

const text = (key: string, required = false): EventField => ({ key, type: 'text', required });
const time = (key: string, required = false): EventField => ({ key, type: 'datetime', required });
const number = (key: string, unit: string): EventField => ({
  key,
  type: 'number',
  required: false,
  unit,
});
/** Tam sayı alanı (ör. römorkör sayısı; aralık şemada). */
const count = (key: string): EventField => ({ key, type: 'number', required: false, decimals: 0 });
const select = (key: string, list: string, values: readonly string[], required = true) => ({
  key,
  type: 'select' as const,
  required,
  options: { list, values },
});
const distance = number('distanceSinceLast', 'NM');
const reason = select('reason', 'reasons', WAITING_REASONS);

/**
 * Sürüm 1 alanları (ADR 0058 §1): eski raporlar bu tabloyla gösterilir. Departure'ın olay zamanı
 * pilot off, `letGo` son halat.
 */
const FIELDS_V1: Partial<Record<EventKind, EventField[]>> = {
  departure: [
    text('port', true),
    text('terminal'),
    time('allFast'),
    time('pilotOnBoard', true),
    time('letGo', true),
    time('underway'),
    // Römorkör sayısı (ADR 0054 §4); adlar ve saatler römorkör tablosunda (ADR 0057 §3)
    count('tugsUsed'),
  ],
  arrival: [text('port', true), time('pilotOnBoard'), time('firstLine'), time('allFast')],
  eosp: [distance],
};

/**
 * Sürüm 2 (ADR 0058): Departure'ın pilot, römorkör, sonraki liman, yük ve sınıflandırma alanları
 * kendi bölümlerinde (`DepartureSections.tsx`); burada yalnız liman ve terminal. Olay zamanı Cast
 * Off. Berthing'de pilot off, yanaşma tarafı ve kıyıya mesafe; EOSP'ta limana mesafe.
 */
const FIELDS: Record<EventKind, EventField[]> = {
  departure: [text('port', true), text('terminal')],
  cosp: [
    // Aralık (0 ≤ x < 360) şemada denetlenir; hata alanda görünür
    number('course', '°'),
    number('rpm', 'rpm'),
    number('speedKn', 'kn'),
    select('loadCondition', 'loadConditions', LOAD_CONDITIONS, false),
  ],
  eosp: [distance, number('distanceToPortNm', 'NM')],
  arrival: [
    text('port', true),
    text('terminal'),
    time('pilotOnBoard'),
    time('firstLine'),
    time('allFast'),
    time('pilotOff'),
    select('berthSide', 'berthSides', BERTH_SIDES, false),
    number('distanceToShoreM', 'm'),
  ],
  anchorage_start: [reason, distance],
  // Seferin günlükleri (ADR 0059 §3): bölümleri `DailySections.tsx`'te; eski tek sayılık tüketim
  // yalnız eski raporda
  port_daily: [],
  anchorage_daily: [{ ...number('consumptionMt', 'MT'), legacy: true }],
  anchorage_end: [],
  waiting_start: [reason, distance],
  waiting_end: [distance],
  drift_start: [reason, distance],
  drift_daily: [distance],
  drift_end: [distance],
  strait_entry: [text('name', true), distance],
  strait_exit: [text('name', true), distance],
  canal_entry: [text('name', true), distance],
  canal_exit: [text('name', true), distance],
  cargo_start: [select('operation', 'cargoOperations', CARGO_OPERATIONS)],
  cargo_complete: [select('operation', 'cargoOperations', CARGO_OPERATIONS)],
  on_hire: [text('charterPartyNo')],
  off_hire: [text('charterPartyNo')],
};

export const eventFields = (
  kind: EventKind,
  schema: number = VOYAGE_REPORT_SCHEMA_VERSION,
): readonly EventField[] => (schema === 1 ? (FIELDS_V1[kind] ?? FIELDS[kind]) : FIELDS[kind]);

/** Türün ortak bölümü var mı (`REPORT_SECTIONS`; ADR 0057 §3). */
export const hasSection = (kind: VoyageReportKind, section: ReportSection) =>
  REPORT_SECTIONS[kind].includes(section);

/** ROB tablosu (yakıt, yağ, tatlı su) girilen türler (ADR 0057 §3; g5 "Bunkers, Lub Oil, FW"). */
export const ROB_KINDS: ReadonlySet<EventKind> = new Set(
  EVENT_KINDS.filter((kind) => hasSection(kind, 'rob')),
);
/** Draft (FWD / AFT) girilen türler (sürüm 1 düzeni; sürüm 2'de gemi durumu bölümünde). */
export const DRAFT_KINDS: ReadonlySet<EventKind> = new Set(['departure']);

export interface EventFormValues extends NoonFormValues {
  kind: EventKind;
  /** Şema sürümü (ADR 0058 §1): kayıtlı rapor; yeni ve yükseltilmiş taslak güncel sürüm. */
  schema?: number;
  /** Raporun seferi; sefersiz kalkışta `null` (ADR 0058 §3). */
  voyageId?: string | null;
}

const blank = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim() === '') ||
  (typeof value === 'object' && !Array.isArray(value) && Object.values(value).every(blank));

/** Tamamen boşsa gönderilmeyen bölümler (ADR 0057 §3, ADR 0058 §2). */
const OPTIONAL_SECTIONS = [
  'freshWater',
  'weather',
  'persons',
  'shipCondition',
  'cargo',
  'nor',
  'mooring',
  'classification',
  'checklist',
  'openDefects',
  'portCall',
  'draft',
  'location',
  'cargoProgress',
] as const;

/**
 * Formdan rapor gövdesine. ROB değeri girilmemiş yakıt ve yağ satırı gönderilmez (tür yalnız
 * öneridir); boş ortak bölüm gönderilmez; römorkör satırı varsa sayı satırlardan türer (Ç4).
 * Gerisi Noon ile aynı (boş alan atılır, konum metinden).
 */
export function eventToInput(
  values: EventFormValues,
  options: { draft?: boolean; fuelLedger?: boolean } = {},
): FormConversion {
  const data = { ...values.data };
  for (const key of ['rob', 'lubes'] as const) {
    if (Array.isArray(data[key])) {
      data[key] = (data[key] as { rob?: unknown }[]).filter((row) => typeof row.rob === 'number');
    }
  }
  // Tatlı su da yakıt satırı gibi: ROB girilmemişse (yalnız önerilen önceki değer) gönderilmez
  const water = data.freshWater as { rob?: unknown } | null | undefined;
  if (water && typeof water.rob !== 'number') delete data.freshWater;
  for (const key of OPTIONAL_SECTIONS) {
    if (blank(data[key])) delete data[key];
  }
  if (Array.isArray(data.tugs)) {
    const tugs = (data.tugs as Record<string, unknown>[]).filter((row) => !blank(row));
    data.tugs = tugs;
    if (tugs.length > 0 && values.kind === 'departure') data.tugsUsed = tugs.length;
  }
  // Günlükte "yük yok" miktarsız (ADR 0059 §3)
  const progress = data.cargoProgress as Record<string, unknown> | null | undefined;
  if (progress?.operation === 'none') {
    data.cargoProgress = { operation: 'none', remarks: progress.remarks ?? null };
  }
  // "Yük yok" satırsız (ADR 0058 §2); pilot kullanılmadıysa zamanlar gönderilmez (N/A)
  const cargo = data.cargo as Record<string, unknown> | null | undefined;
  if (cargo?.operation === 'none') data.cargo = { ...cargo, lines: [] };
  if (data.pilotNotUsed === true) {
    delete data.pilotOnBoard;
    delete data.pilotOff;
  } else delete data.pilotNotUsed;
  const schema = values.schema ?? VOYAGE_REPORT_SCHEMA_VERSION;
  return valuesToInput({ ...values, data }, { ...options, kind: values.kind, schema });
}

/**
 * Sürüm 1 Departure taslağının yeni biçime yükseltilmesi (ADR 0058 §1): olay zamanı son halat
 * (`letGo`), eski olay zamanı (pilot off) `pilotOff` olur; `letGo`, `underway`, `allFast` düşer.
 * Rapor zaten yeniden gönderilecek (içerik değişir).
 */
export function upgradeDeparture(values: EventFormValues): EventFormValues {
  if (values.kind !== 'departure' || (values.schema ?? 1) !== 1) return values;
  const { letGo, underway: _u, allFast: _a, ...data } = values.data;
  return {
    ...values,
    schema: VOYAGE_REPORT_SCHEMA_VERSION,
    reportedAt: typeof letGo === 'string' ? letGo : values.reportedAt,
    data: {
      ...data,
      pilotOff: values.reportedAt,
      voyageAction: values.voyageId ? 'link' : (data.voyageAction ?? null),
    },
  };
}

const byTime = (a: VoyageReport, b: VoyageReport) =>
  Date.parse(a.reportedAt) - Date.parse(b.reportedAt);

/**
 * Yeni olayın başlangıç değerleri: saat şimdi (dakikaya), saat farkı son rapordan (yoksa geminin
 * saat diliminden), ROB satırlarının yakıt türleri son ROB'lu rapordan ya da seferin makine
 * yakıtlarından. ROB değeri önerilmez: elle girilir.
 */
export function newEventValues(
  kind: EventKind,
  reports: readonly VoyageReport[],
  now: Date,
  zoneOffset: number,
  voyage:
    | (Pick<Voyage, 'mainEngineFuel' | 'auxEngineFuel'> &
        Partial<Pick<Voyage, 'id' | 'fromPort' | 'fromPortCode' | 'toPort' | 'toPortCode'>> &
        Partial<Pick<Voyage, 'charterer' | 'charterPartyNo'>>)
    | null,
  extra: { crewOnBoard?: number | null } = {},
): EventFormValues {
  const sorted = [...reports].sort(byTime);
  const tzOffsetMin = sorted.at(-1)?.tzOffsetMin ?? zoneOffset;
  const reportedAt = new Date(Math.floor(now.getTime() / 60_000) * 60_000).toISOString();
  const data: Record<string, unknown> = {};
  if (ROB_KINDS.has(kind)) {
    // Önceki ROB son rapordan (Noon'da kapanış): yalnız "Previous" sütununa (ADR 0057 §3)
    const previous = previousRob(sorted);
    const fuels =
      previous.fuels.size > 0
        ? [...previous.fuels.keys()]
        : [voyage?.mainEngineFuel, voyage?.auxEngineFuel].filter((f): f is string => !!f);
    // Günlükte "bugün" sütunu da (ADR 0059 §3)
    const today = isDailyKind(kind) ? { todayMt: null } : {};
    data.rob = [...new Set(fuels)].map((fuel) => ({
      fuel,
      previousMt: previous.fuels.get(fuel) ?? null,
      consumptionMt: null,
      ...today,
      rob: null,
    }));
    data.lubes = [...previous.lubes].map(([lube, mt]) => ({
      lube,
      previousMt: mt,
      consumptionMt: null,
      ...today,
      rob: null,
    }));
    if (previous.freshWater !== null) {
      data.freshWater = {
        previousMt: previous.freshWater,
        consumptionMt: null,
        ...today,
        rob: null,
      };
    }
  }
  let position = { lat: '', lon: '' };
  if (isDailyKind(kind)) {
    const daily = dailyDefaults(kind, sorted, reportedAt);
    Object.assign(data, daily.data);
    position = daily.position;
  }
  // Next Operation açıkça yazılır (formun önceki değeri kalmasın): tek seçenek önceden seçili
  const options = nextOperationOptions(kind);
  data.nextOperation = options.length === 1 ? options[0] : null;
  if (kind === 'departure' || kind === 'arrival') {
    Object.assign(data, portDefaults(kind, sorted, voyage));
    data.tugs = [];
    if (typeof extra.crewOnBoard === 'number' && extra.crewOnBoard > 0) {
      data.persons = { crew: extra.crewOnBoard, passengers: null };
    }
  }
  if (kind === 'departure') {
    // Sefer kararı (ADR 0058 §3): seferde açılan kalkış seferindir; sefersizde karar beklenir
    data.voyageAction = voyage?.id ? 'link' : null;
    data.cargo = { operation: null, lines: [] };
    data.classification = voyage?.charterer
      ? { employment: 'chartered', charterReference: voyage.charterPartyNo ?? null }
      : {};
  }
  return {
    kind,
    reportedAt,
    tzOffsetMin,
    ...position,
    data,
    schema: VOYAGE_REPORT_SCHEMA_VERSION,
    voyageId: voyage?.id ?? null,
  };
}

const DAILY_ACTIVITY: Record<string, string> = {
  port_daily: 'alongside',
  anchorage_daily: 'at_anchor',
  drift_daily: 'drifting',
};

const recordOf = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

/** Çalışma saati satırı önerisi: ekipman ve önceki toplam; son rapordan beri ve bugün elle. */
const hourRow = (equipment: string, totalH: unknown) => ({
  equipment,
  sinceLastH: null,
  todayH: null,
  totalH: typeof totalH === 'number' ? totalH : null,
  remarks: null,
});

/** Son Noon'un makineleri: ana makine ve yardımcılar, toplam sayaçlarıyla. */
function noonHourRows(sorted: readonly VoyageReport[]) {
  const noon = sorted.filter((r) => r.kind === 'noon').at(-1);
  if (!noon) return [];
  const machinery = recordOf(noon.data.machinery);
  const engine = recordOf(machinery.mainEngine);
  const auxiliaries = Array.isArray(machinery.auxiliaries)
    ? (machinery.auxiliaries as Record<string, unknown>[])
    : [];
  return [
    ...(typeof engine.runningHours === 'number'
      ? [hourRow('Main Engine', engine.runningHours)]
      : []),
    ...auxiliaries
      .filter((row) => typeof row.name === 'string')
      .map((row) => hourRow(String(row.name), row.runningHours)),
  ];
}

/**
 * Aynı aşamadaki (son Berthing, demir ya da drift başlangıcından beri) önceki günlük; yoksa
 * `undefined`.
 */
export function previousDaily(
  kind: EventKind,
  reports: readonly VoyageReport[],
  at: string,
): VoyageReport | undefined {
  const phase = phaseReference(kind, reports, at);
  const since = phase ? Date.parse(phase.reportedAt) : Number.NEGATIVE_INFINITY;
  const until = Date.parse(at);
  return [...reports]
    .sort(byTime)
    .filter((r) => r.kind === kind)
    .filter((r) => Date.parse(r.reportedAt) >= since && Date.parse(r.reportedAt) < until)
    .at(-1);
}

/**
 * Çalışma saatlerinin önceki toplamları (ekipman adı küçük harfle): önceki günlükten, yoksa son
 * Noon'un makinelerinden. Formda "toplam = önceki + son rapordan beri" önerisi için.
 */
export function previousRunningHours(
  kind: EventKind,
  reports: readonly VoyageReport[],
  at: string,
): Map<string, number> {
  const before = [...reports].sort(byTime).filter((r) => Date.parse(r.reportedAt) < Date.parse(at));
  const previous = previousDaily(kind, before, at);
  const rows = Array.isArray(previous?.data.runningHours)
    ? (previous.data.runningHours as Record<string, unknown>[]).map((row) =>
        hourRow(String(row.equipment ?? ''), row.totalH),
      )
    : noonHourRows(before);
  const totals = new Map<string, number>();
  for (const row of rows) {
    if (row.totalH !== null) totals.set(row.equipment.trim().toLocaleLowerCase('en'), row.totalH);
  }
  return totals;
}

/**
 * Yeni günlüğün önerileri (ADR 0059 §4): aynı aşamadaki önceki günlükten (konum durumu, ETD, yük
 * planı ve toplamı, çalışma saati satırları); yoksa aşamanın başlangıcından (Berthing'in limanı,
 * terminali, yanaşma tarafı) ve son Noon'un makinelerinden. Rıhtımda ve demirde konum
 * başlangıcınki; drift'te önerilmez.
 */
function dailyDefaults(
  kind: EventKind,
  sorted: readonly VoyageReport[],
  at: string,
): { data: Record<string, unknown>; position: { lat: string; lon: string } } {
  const phase = phaseReference(kind, sorted, at);
  const previous = previousDaily(kind, sorted, at);
  const data: Record<string, unknown> = {};
  if (previous) {
    const before = recordOf(previous.data.location);
    data.location = {
      port: before.port ?? null,
      portCode: before.portCode ?? null,
      area: before.area ?? null,
      activity: before.activity ?? DAILY_ACTIVITY[kind],
      berthSide: before.berthSide ?? null,
    };
    const progress = recordOf(previous.data.cargoProgress);
    data.etd = previous.data.etd ?? null;
    data.cargoProgress = {
      operation: progress.operation ?? null,
      cargoType: progress.cargoType ?? null,
      unit: progress.unit ?? null,
      loadCondition: progress.loadCondition ?? null,
      plannedQty: progress.plannedQty ?? null,
      handledSinceLastQty: null,
      handledTotalQty: progress.handledTotalQty ?? null,
      remarks: null,
    };
  } else {
    data.location = {
      port: phase?.port ?? null,
      portCode: phase?.portCode ?? null,
      area: phase?.terminal ?? null,
      activity: DAILY_ACTIVITY[kind],
      berthSide: phase?.berthSide ?? null,
    };
  }
  const lastHours = Array.isArray(previous?.data.runningHours)
    ? (previous.data.runningHours as Record<string, unknown>[])
    : null;
  data.runningHours = lastHours
    ? lastHours
        .filter((row) => typeof row.equipment === 'string')
        .map((row) => hourRow(String(row.equipment), row.totalH))
    : noonHourRows(sorted);
  // Rıhtımdaki ve demirdeki gemi yerinde durur: konum önceki günlükten ya da başlangıçtan
  const anchor = previous ?? (phase ? sorted.find((r) => r.id === phase.id) : undefined);
  const position =
    kind !== 'drift_daily' && anchor && anchor.lat !== null && anchor.lon !== null
      ? { lat: formatLatitude(anchor.lat), lon: formatLongitude(anchor.lon) }
      : { lat: '', lon: '' };
  return { data, position };
}

/**
 * Limanın önerisi: Departure son Berthing'in limanından (ara liman) ya da seferin kalkış
 * limanından, sonraki liman seferin varışı; Berthing seferin varış limanı.
 */
function portDefaults(
  kind: 'departure' | 'arrival',
  sorted: readonly VoyageReport[],
  voyage: Partial<Pick<Voyage, 'fromPort' | 'fromPortCode' | 'toPort' | 'toPortCode'>> | null,
): Record<string, unknown> {
  if (kind === 'arrival') {
    return voyage?.toPort ? { port: voyage.toPort, portCode: voyage.toPortCode ?? null } : {};
  }
  const berth = sorted.filter((r) => r.kind === 'arrival').at(-1);
  const port = berth
    ? { port: berth.data.port ?? null, portCode: berth.data.portCode ?? null }
    : voyage?.fromPort
      ? { port: voyage.fromPort, portCode: voyage.fromPortCode ?? null }
      : {};
  const next = voyage?.toPort
    ? { nextPort: voyage.toPort, nextPortCode: voyage.toPortCode ?? null }
    : {};
  return { ...port, ...next };
}

const numberOf = (value: unknown) => (typeof value === 'number' ? value : null);

/** Noon'un tatlı su kapanışı ya da olayın tatlı su ROB'u. */
function waterOf(report: VoyageReport): number | null {
  if (report.kind !== 'noon') {
    return numberOf((report.data.freshWater as Record<string, unknown> | undefined)?.rob);
  }
  const water = (report.data.fuel as { freshWater?: Record<string, unknown> } | undefined)
    ?.freshWater;
  const rob = numberOf(water?.rob);
  if (!water || rob === null) return null;
  return freshWaterClosing({
    rob,
    consumption: numberOf(water.consumption) ?? 0,
    produced: numberOf(water.produced) ?? 0,
    bunkered: numberOf(water.bunkered) ?? 0,
  });
}

/**
 * Son bilinen ROB (ADR 0057 §3): yakıtlar ROB'u olan en yeni rapordan (Noon'da kapanış), yağlar
 * `lubes`'u olan en yeni rapordan, tatlı su tatlı suyu olan en yeni rapordan.
 */
export function previousRob(reports: readonly VoyageReport[]): {
  fuels: Map<string, number>;
  lubes: Map<string, number>;
  freshWater: number | null;
} {
  let fuels = new Map<string, number>();
  const lubes = new Map<string, number>();
  let freshWater: number | null = null;
  for (const report of [...reports].sort(byTime).reverse()) {
    if (fuels.size === 0) fuels = reportRob(report);
    if (lubes.size === 0 && Array.isArray(report.data.lubes)) {
      for (const row of report.data.lubes as Record<string, unknown>[]) {
        const rob = numberOf(row.rob);
        if (typeof row.lube === 'string' && rob !== null) lubes.set(row.lube, rob);
      }
    }
    freshWater ??= waterOf(report);
    if (fuels.size > 0 && lubes.size > 0 && freshWater !== null) break;
  }
  return { fuels, lubes, freshWater };
}

/**
 * Kayıtlı olayın form değerleri. `upgrade`: düzenlenecek (taslak ya da yeniden açılmış) sürüm 1
 * Departure yeni biçime yükseltilir (ADR 0058 §1); salt okunur rapor kendi sürümüyle gösterilir.
 */
export function eventValues(
  report: VoyageReport,
  options: { upgrade?: boolean } = {},
): EventFormValues {
  const values: EventFormValues = {
    kind: report.kind as EventKind,
    reportedAt: report.reportedAt,
    tzOffsetMin: report.tzOffsetMin,
    lat: formatLatitude(report.lat),
    lon: formatLongitude(report.lon),
    data: { nextOperation: null, ...report.data },
    schema: report.schema,
    voyageId: report.voyageId,
  };
  return options.upgrade ? upgradeDeparture(values) : values;
}
